import React from "react";
import { isLedgerMode } from "../../../utils";
import { CompositionLegend } from "./CompositionLegend";
import {
  ValueCompositionProps,
  CompositionSegment,
  CompositionRow,
} from "./types";
import { CompositionCard, CompositionTitle, Bar, BarSegment } from "./styles";

const INVESTED_COLOR = "var(--color-warning)";
const GAIN_COLOR = "var(--color-success)";
const LOSS_COLOR = "var(--color-error)";
const WITHDRAWN_COLOR = "var(--color-gray-600)";
const SECURITIES_COLOR = "var(--color-info)";
const CASH_COLOR = "var(--color-success)";

/**
 * Displays how an investment's value breaks down. Three layouts are produced
 * depending on state: a default unrealized-gain view, an unrealized-loss view
 * (invested capital with the lost portion in red), and a fully-withdrawn view
 * that surfaces the realized gain or loss instead of the remaining value.
 */
const ValueCompositionComponent: React.FC<ValueCompositionProps> = ({
  trackingMode,
  invested,
  gain,
  withdrawn,
  currentValue,
  cashBalance,
  securitiesValue,
  showsCash = true,
}) => {
  if (isLedgerMode(trackingMode)) {
    const canCompose = securitiesValue !== null && cashBalance !== null;
    const hasNegativeCash = cashBalance !== null && cashBalance < 0;

    // The bar composes against the legs' own sum. It deliberately does not take
    // the row's `totalValue`: that is the *selected* account value, which for a
    // settlement-only ledger is the securities leg alone, so dividing by it would
    // draw a cash share of a total that does not contain the cash — a bar wider
    // than 100% labelled 100% / 0%.
    const composedTotal =
      canCompose && !hasNegativeCash ? securitiesValue + cashBalance : 0;

    // A negative cash leg has no share of the total to draw, and neither does a
    // total of zero or less — both would be a proportion of nothing. In those
    // cases the bar shows the securities leg alone and no percentages at all,
    // rather than inventing shares that do not add up.
    let securitiesWidth = 0;
    let cashWidth = 0;
    let securitiesPercent: number | undefined;
    let cashPercent: number | undefined;
    if (canCompose && !hasNegativeCash && composedTotal > 0) {
      securitiesWidth = (securitiesValue / composedTotal) * 100;
      cashWidth = (cashBalance / composedTotal) * 100;
      // Round one leg and give the other the remainder, so the visible shares
      // always describe the whole bar rather than occasionally adding to 99% or
      // 101% through independent rounding.
      securitiesPercent = Math.round(securitiesWidth);
      cashPercent = 100 - securitiesPercent;
    } else if (canCompose && securitiesValue > 0) {
      securitiesWidth = 100;
    }

    let cashSwatchColor: string | undefined;
    if (cashBalance !== null) {
      cashSwatchColor = hasNegativeCash ? LOSS_COLOR : CASH_COLOR;
    }

    const allRows: CompositionRow[] = [
      {
        key: "securities",
        label: "Securities",
        amount: securitiesValue,
        testId: "composition-securities",
        swatchColor: securitiesValue === null ? undefined : SECURITIES_COLOR,
        percent: securitiesPercent,
      },
      {
        key: "cash",
        label: "EUR cash",
        amount: cashBalance,
        testId: "composition-cash",
        swatchColor: cashSwatchColor,
        amountColor: hasNegativeCash ? LOSS_COLOR : undefined,
        percent: cashPercent,
      },
    ];
    const rows = showsCash
      ? allRows
      : allRows.filter((row) => row.key !== "cash");

    return (
      <CompositionCard role="region" aria-label="Brokerage composition">
        <CompositionTitle>Brokerage composition</CompositionTitle>

        {canCompose && showsCash && (
          <Bar data-testid="brokerage-composition-bar">
            <BarSegment
              $width={securitiesWidth}
              $offset={0}
              $color={SECURITIES_COLOR}
            />
            <BarSegment
              $width={cashWidth}
              $offset={securitiesWidth}
              $color={CASH_COLOR}
            />
          </Bar>
        )}

        <CompositionLegend rows={rows} />
      </CompositionCard>
    );
  }

  if (gain === null) {
    return null;
  }

  const isFullyWithdrawn = withdrawn > 0 && currentValue <= 0;
  const isLoss = gain < 0;

  let segments: CompositionSegment[];
  let rows: CompositionRow[];

  if (isFullyWithdrawn) {
    const isRealizedLoss = gain < 0;
    segments = [{ width: 100, color: WITHDRAWN_COLOR }];
    rows = [
      {
        key: "invested",
        label: "Invested",
        amount: invested,
        testId: "composition-invested",
        swatchColor: INVESTED_COLOR,
      },
      {
        key: "withdrawn",
        label: "Withdrawn",
        amount: withdrawn,
        testId: "composition-withdrawn",
        swatchColor: WITHDRAWN_COLOR,
      },
      {
        key: "realized",
        label: isRealizedLoss ? "Realized loss" : "Realized gain",
        amount: gain,
        testId: "composition-realized",
        amountColor: isRealizedLoss ? LOSS_COLOR : GAIN_COLOR,
        divided: true,
      },
    ];
  } else if (isLoss) {
    const lossMagnitude = Math.abs(gain);
    const lossWidth =
      invested > 0 ? Math.min((lossMagnitude / invested) * 100, 100) : 100;
    const investedWidth = 100 - lossWidth;
    segments = [
      { width: investedWidth, color: INVESTED_COLOR },
      { width: lossWidth, color: LOSS_COLOR },
    ];
    rows = [
      {
        key: "invested",
        label: "Invested",
        amount: invested,
        testId: "composition-invested",
        swatchColor: INVESTED_COLOR,
        percent: Math.round(investedWidth),
      },
      {
        key: "loss",
        label: "Unrealized loss",
        amount: gain,
        testId: "composition-gain",
        swatchColor: LOSS_COLOR,
        amountColor: LOSS_COLOR,
        percent: Math.round(lossWidth),
      },
      {
        key: "withdrawn",
        label: "Withdrawn",
        amount: withdrawn,
        testId: "composition-withdrawn",
        swatchColor: WITHDRAWN_COLOR,
        divided: true,
      },
    ];
  } else {
    const barBase = invested + gain;
    const investedWidth = barBase > 0 ? (invested / barBase) * 100 : 100;
    const gainWidth = 100 - investedWidth;
    segments = [
      { width: investedWidth, color: INVESTED_COLOR },
      { width: gainWidth, color: GAIN_COLOR },
    ];
    rows = [
      {
        key: "invested",
        label: "Invested",
        amount: invested,
        testId: "composition-invested",
        swatchColor: INVESTED_COLOR,
        percent: Math.round(investedWidth),
      },
      {
        key: "gain",
        label: "Unrealized gain",
        amount: gain,
        testId: "composition-gain",
        swatchColor: GAIN_COLOR,
        percent: Math.round(gainWidth),
      },
      {
        key: "withdrawn",
        label: "Withdrawn",
        amount: withdrawn,
        testId: "composition-withdrawn",
        swatchColor: WITHDRAWN_COLOR,
        divided: true,
      },
    ];
  }

  let runningOffset = 0;
  const stackedSegments = segments.map((segment) => {
    const offset = runningOffset;
    runningOffset += segment.width;
    return { ...segment, offset };
  });

  return (
    <CompositionCard role="region" aria-label="Value composition">
      <CompositionTitle>Value Composition</CompositionTitle>

      <Bar data-testid="value-composition-bar">
        {stackedSegments.map((segment) => (
          <BarSegment
            key={segment.color}
            $width={segment.width}
            $offset={segment.offset}
            $color={segment.color}
          />
        ))}
      </Bar>

      <CompositionLegend rows={rows} />
    </CompositionCard>
  );
};

export const ValueComposition = React.memo(ValueCompositionComponent);
